import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Table,
  ColumnTable,
  addToast,
  Icon,
  Modal,
  FileInput,
  showDialogConfirm,
  useMobile,
} from '@octano/global-ui';

import SectionTitle from '../../../../../../components/text/SectionTitle';
import { TranslationsKeys } from '../../../../../../locales/translations';
import CourseFileBadge from '../../../../Request/parts/CourseFileBadge';
import CourseStatusBadge, {
  CourseStatus,
} from '../../../../Request/parts/CourseStatusBadge';
import {
  uploadCourseFile,
  downloadCourseFile,
  deleteCourseFile,
} from '../../../../Request/api';
import { HomologationSelectedCourse } from '../../../../Request/types';

interface Props {
  courses: HomologationSelectedCourse[];
  requestId: number;
  onHomologate: (course: HomologationSelectedCourse) => void;
  onFileChange: () => void;
  readOnly?: boolean;
}

export default function HomologationCoursesTable({
  courses,
  requestId,
  onHomologate,
  onFileChange,
  readOnly = false,
}: Props) {
  const { t } = useTranslation(
    TranslationsKeys.HOMOLOGATION_AUTHORIZATION_DETAIL,
  );
  const isMobile = useMobile();

  const [uploadModalCourseId, setUploadModalCourseId] = useState<number | null>(
    null,
  );
  const [uploadFile, setUploadFile] = useState<File | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [downloadingCourseId, setDownloadingCourseId] = useState<number | null>(
    null,
  );

  const handleOpenUploadModal = useCallback((courseId: number) => {
    setUploadModalCourseId(courseId);
    setUploadFile(undefined);
    setIsSaving(false);
  }, []);

  const handleCloseUploadModal = useCallback(() => {
    setUploadModalCourseId(null);
    setUploadFile(undefined);
    setIsSaving(false);
  }, []);

  const handleSaveFile = useCallback(async () => {
    if (!uploadFile || !uploadModalCourseId) {
      return;
    }

    setIsSaving(true);
    const result = await uploadCourseFile(
      requestId,
      uploadModalCourseId,
      uploadFile,
    );

    if (result.error) {
      setIsSaving(false);
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('coursesTable.uploadModal.errorToast'),
      });
      return;
    }

    addToast({
      icon: 'success',
      color: 'success',
      text: t('coursesTable.uploadModal.successToast'),
    });
    handleCloseUploadModal();
    onFileChange();
  }, [
    requestId,
    uploadModalCourseId,
    uploadFile,
    onFileChange,
    handleCloseUploadModal,
    t,
  ]);

  const handleDownload = useCallback(
    async (courseId: number) => {
      setDownloadingCourseId(courseId);
      const result = await downloadCourseFile(requestId, courseId);
      setDownloadingCourseId(null);

      if (result.error || !result.data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('coursesTable.downloadErrorToast'),
        });
        return;
      }
      window.open(result.data.url, '_blank');
    },
    [requestId, t],
  );

  const handleDelete = useCallback(
    (courseId: number) => {
      showDialogConfirm({
        icon: {
          color: 'warning',
          name: 'warning',
        },
        title: t('coursesTable.deleteConfirm.title'),
        subtitle: t('coursesTable.deleteConfirm.subtitle'),
        btnClose: {
          text: t('coursesTable.deleteConfirm.cancel'),
        },
        btnConfirm: {
          text: t('coursesTable.deleteConfirm.confirm'),
          onConfirm: async () => {
            const result = await deleteCourseFile(requestId, courseId);

            if (result.error) {
              addToast({
                icon: 'error',
                color: 'danger',
                text: t('coursesTable.deleteErrorToast'),
              });
              return;
            }

            addToast({
              icon: 'success',
              color: 'success',
              text: t('coursesTable.deleteSuccessToast'),
            });
            onFileChange();
          },
        },
      });
    },
    [requestId, onFileChange, t],
  );

  const columns: ColumnTable<HomologationSelectedCourse>[] = useMemo(
    () => [
      {
        columnName: 'courseName',
        headerText: t('coursesTable.courseName'),
        thClassName: 'text-left',
        tdClassName: 'text-left',
        cellFormat: ({ row }) => (
          <div>
            <span className="fw-bold">{row.courseName}</span>
            <br />
            <small className="text-muted">{row.courseCode}</small>
          </div>
        ),
      },
      {
        columnName: 'programFile',
        headerText: t('coursesTable.programFile'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const hasFile = !!row.courseResult?.linkedFileId;
          const isDownloading = downloadingCourseId === row.semesterCourseId;

          return (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ gap: 12 }}
            >
              <CourseFileBadge
                loaded={hasFile}
                loadedLabel={t('coursesTable.fileStatus.loaded')}
                notLoadedLabel={t('coursesTable.fileStatus.notLoaded')}
              />
              {!readOnly && (
                <button
                  className="btn btn-link p-0"
                  title={t('coursesTable.uploadFile')}
                  onClick={() => handleOpenUploadModal(row.semesterCourseId)}
                  style={{ color: '#1976d2' }}
                >
                  <Icon name="upload" size="16px" />
                </button>
              )}
              <button
                className="btn btn-link p-0"
                title={t('coursesTable.downloadFile')}
                onClick={() => handleDownload(row.semesterCourseId)}
                disabled={!hasFile || isDownloading}
                style={{ color: hasFile ? '#1976d2' : '#ccc' }}
              >
                {isDownloading ? (
                  <Icon className="spin" name="circle" size="16px" />
                ) : (
                  <Icon name="download" size="16px" />
                )}
              </button>
              {!readOnly && (
                <button
                  className="btn btn-link p-0"
                  title={t('coursesTable.deleteFile')}
                  onClick={() => handleDelete(row.semesterCourseId)}
                  disabled={!hasFile}
                  style={{ color: hasFile ? '#f44336' : '#ccc' }}
                >
                  <Icon name="trash" size="16px" />
                </button>
              )}
            </div>
          );
        },
      },
      {
        columnName: 'status',
        headerText: t('coursesTable.requestStatus'),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <CourseStatusBadge status={row.status as CourseStatus} />
        ),
      },
      {
        columnName: 'observation',
        headerText: t('coursesTable.observation'),
        thClassName: 'text-left',
        tdClassName: 'text-left',
        cellFormat: ({ row }) => (
          <span className="text-muted">{row.observation || '-'}</span>
        ),
      },
      ...(!readOnly
        ? [
            {
              columnName: 'actions',
              headerText: t('coursesTable.action'),
              thClassName: 'text-center',
              tdClassName: 'text-center',
              cellFormat: ({ row }: { row: HomologationSelectedCourse }) => (
                <Button
                  text={t('coursesTable.homologate')}
                  size="sm"
                  onClick={() => onHomologate(row)}
                />
              ),
            },
          ]
        : []),
    ],
    [
      t,
      onHomologate,
      handleOpenUploadModal,
      handleDownload,
      handleDelete,
      downloadingCourseId,
      readOnly,
    ],
  );

  return (
    <div className="mt-4">
      <div className="mb-3">
        <SectionTitle text={t('coursesTable.title')} />
      </div>
      <Table data={courses} columns={columns} />

      {/* Upload File Modal */}
      <Modal
        isOpen={uploadModalCourseId !== null}
        toggle={() => null}
        onClosed={() => setUploadFile(undefined)}
      >
        <div>
          <h1 className="text-dark text-center fw-600 fs-20">
            {t('coursesTable.uploadModal.title')}
          </h1>
          <p className="text-center fs-16 lh-30 my-3">
            {t('coursesTable.uploadModal.description')}
          </p>
          <FileInput
            btnText={t('coursesTable.uploadModal.loadBtn')}
            value={uploadFile}
            name="file"
            label={t('coursesTable.uploadModal.fileLabel')}
            onChange={(f) => setUploadFile(f)}
            accept=".jpg,.png,.pdf,.jpeg,.docx"
            disabled={isSaving}
          />
          <div
            className="d-flex flex-wrap justify-content-between mt-4"
            style={{ gap: '1rem' }}
          >
            <Button
              text={t('coursesTable.uploadModal.closeBtn')}
              outlined
              style={{ width: isMobile ? '100%' : 197 }}
              onClick={handleCloseUploadModal}
              disabled={isSaving}
            />
            <Button
              text={t('coursesTable.uploadModal.saveBtn')}
              style={{ width: isMobile ? '100%' : 197 }}
              onClick={handleSaveFile}
              loading={isSaving}
              disabled={!uploadFile}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
